// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

// ============================================================
// Innovation Hub Navigation Configuration
// ============================================================
const NAV_ITEMS = {
  owner: [
    { section: 'Command' },
    { path: '/overwatch', icon: '\uD83D\uDC41', label: 'Overwatch' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { section: 'Innovation Engine' },
    { path: '/ai-intake', icon: '\uD83E\uDDE0', label: 'AI Intake' },
    { path: '/ideas', icon: '\uD83D\uDCA1', label: 'Idea Marketplace' },
    { path: '/app-generator', icon: '\u2699\uFE0F', label: 'App Generator' },
    { path: '/website-builder', icon: '\uD83C\uDF10', label: 'Website Builder' },
    { path: '/logo-generator', icon: '\uD83C\uDFA8', label: 'Logo Generator' },
    { section: 'Legal & IP' },
    { path: '/legal-generator', icon: '\uD83D\uDCC4', label: 'Legal Doc Generator' },
    { path: '/patent-generator', icon: '\uD83D\uDCDC', label: 'Patent Generator' },
    { section: 'Business' },
    { path: '/portfolio', icon: '\uD83D\uDCBC', label: 'App Portfolio' },
    { path: '/co-dev', icon: '\uD83E\uDD1D', label: 'Co-Dev Deals' },
    { path: '/customers', icon: '\uD83D\uDC65', label: 'Clients' },
    { path: '/invoicing', icon: '\uD83D\uDCB0', label: 'Invoicing' },
    { section: 'Compliance' },
    { path: '/compliance', icon: '\uD83D\uDEE1\uFE0F', label: 'Compliance' },
    { path: '/reports', icon: '\uD83D\uDCC8', label: 'Reports' },
    { section: 'System' },
    { path: '/profile', icon: '\u2699\uFE0F', label: 'Settings' },
  ],
  inventor: [
    { section: 'My Hub' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { path: '/ai-intake', icon: '\uD83E\uDDE0', label: 'Submit Idea' },
    { path: '/ideas', icon: '\uD83D\uDCA1', label: 'Marketplace' },
    { section: 'Services' },
    { path: '/app-generator', icon: '\u2699\uFE0F', label: 'Build My App' },
    { path: '/website-builder', icon: '\uD83C\uDF10', label: 'Build My Website' },
    { path: '/logo-generator', icon: '\uD83C\uDFA8', label: 'Design My Logo' },
    { path: '/legal-generator', icon: '\uD83D\uDCC4', label: 'Legal Docs' },
    { path: '/patent-generator', icon: '\uD83D\uDCDC', label: 'File Patent' },
    { section: 'Account' },
    { path: '/invoicing', icon: '\uD83D\uDCB0', label: 'My Invoices' },
    { path: '/profile', icon: '\u2699\uFE0F', label: 'Profile' },
  ],
  demo: [
    { section: 'Overview' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { path: '/ai-intake', icon: '\uD83E\uDDE0', label: 'AI Intake' },
    { path: '/ideas', icon: '\uD83D\uDCA1', label: 'Marketplace' },
    { path: '/app-generator', icon: '\u2699\uFE0F', label: 'App Generator' },
    { path: '/website-builder', icon: '\uD83C\uDF10', label: 'Website Builder' },
    { path: '/logo-generator', icon: '\uD83C\uDFA8', label: 'Logo Generator' },
    { path: '/legal-generator', icon: '\uD83D\uDCC4', label: 'Legal Docs' },
    { path: '/patent-generator', icon: '\uD83D\uDCDC', label: 'Patents' },
    { path: '/portfolio', icon: '\uD83D\uDCBC', label: 'Portfolio' },
    { path: '/profile', icon: '\u2699\uFE0F', label: 'Settings' },
  ],
};

// ============================================================
// ADMIN & OVERWATCH Teams
// ============================================================
const ADMIN_TEAM = [
  'J.lutz@miskulintechnologies.com',
  'R.miskulin@miskulintechnologies.com',
  'rmiskulin@gmail.com',
  'criodtech@gmail.com',
  'threewayindustries@gmail.com',
  'desbachynski@gmail.com',
  'lucas.s.mason@gmail.com',
  'leemehling@hotmail.com',
  'cloricer77@gmail.com',
  'tyson@canstorr.com',
  'tyler.hodgson32@gmail.com',
  'jhansen860@yahoo.com',
  'littlesderek@gmail.com',
  'laken.weston@live.ca',
  'che@rosebudcannabisfarms.ca',
  'shanon@lanewaymedia.ca',
  'amandakristaleigh@hotmail.com',
  'dblahut@sastel.net',
  'demo@iqapps.dev',
  // Mt Forge team
  'j.lutz@mtforge.ai',
  'r.miskulin@mtforge.ai',
  't.schenher@mtforge.ai',
  'c.leblanc@mtforge.ai',
  'd.bachynski@mtforge.ai',
];

const OVERWATCH_TEAM = ADMIN_TEAM;

function getEffectiveRole(user) {
  if (!user) return 'inventor';
  if (ADMIN_TEAM.map(e => e.toLowerCase()).includes((user.email || '').toLowerCase())) return 'owner';
  if (user.email === 'demo@iqapps.dev') return 'demo';
  return user.role || 'inventor';
}

export default function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewAsRole, setViewAsRole] = useState(null);
  const actualRole = getEffectiveRole(user);
  const role = viewAsRole || actualRole;
  let navItems = NAV_ITEMS[role] || NAV_ITEMS.owner;
  if (!OVERWATCH_TEAM.map(e => e.toLowerCase()).includes((user?.email || '').toLowerCase())) {
    navItems = navItems.filter(item => item.path !== '/overwatch');
  }
  const initials = user?.full_name?.split(' ').map(n => n[0]).join('') || 'U';
  const companyLabel = 'Innovation Hub';

  return (
    <div className="app-layout">
      <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '\u2715' : '\u2630'}
      </button>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">{companyLabel}</div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item, i) =>
            item.section ? (
              <div key={i} className="nav-section">{item.section}</div>
            ) : (
              <NavLink key={item.path} to={item.path} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </NavLink>
            )
          )}
          {actualRole === 'owner' && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8', marginBottom: 8 }}>
                View As Role
              </div>
              {Object.keys(NAV_ITEMS).filter(r => r !== 'owner' && r !== 'demo').map(r => (
                <button key={r} onClick={() => setViewAsRole(viewAsRole === r ? null : r)}
                  style={{
                    display: 'block', width: '100%', padding: '6px 10px', marginBottom: 4,
                    background: viewAsRole === r ? '#4F46E5' : 'rgba(255,255,255,0.05)',
                    color: viewAsRole === r ? '#fff' : '#94a3b8',
                    border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                    textAlign: 'left', textTransform: 'capitalize'
                  }}
                >
                  {viewAsRole === r ? '\u2713 ' : ''}{r.replace(/_/g, ' ')}
                </button>
              ))}
              {viewAsRole && (
                <button onClick={() => setViewAsRole(null)}
                  style={{ display: 'block', width: '100%', padding: '6px 10px', marginTop: 4, background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', textAlign: 'center', fontWeight: 700 }}
                >Reset to Owner</button>
              )}
            </div>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{initials}</div>
            <div>
              <div className="user-name">{user?.full_name || user?.email}</div>
              <div className="user-role">{viewAsRole ? `Viewing as: ${viewAsRole.replace(/_/g, ' ')}` : role.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <button className="btn-logout" onClick={() => { signOut(); navigate('/login'); }}>Logout</button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
